/**
 * @file buy-x-get-y-offer.tsx
 * @module app/modules/products/edit-product-variant-price/buy-x-get-y-offer
 * @description Buy X Get Y free-quantity offer form fields and helpers for variant price screens.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { Col, Row } from 'react-bootstrap';
import CustomDatePicker from '../../../custom/DateRange/DatePicker';
import { getBusinessDay } from '../../orders-delivery/utils/free-quantity';

export const DiscountModeNone = '0';
export const DiscountModeByQuantity = '1';
export const DiscountModeBuyXGetY = '2';

export interface BuyXGetYOffer {
  buyQty: string;
  freeQty: string;
  validFrom: string;
  validTo: string;
  isRepeatable?: boolean;
  touched?: boolean;
}

interface BuyXGetYApiOffer {
  buyQty?: number;
  freeQty?: number;
  validFrom?: string;
  validTo?: string;
  isRepeatable?: boolean;
  active?: boolean | string;
}

interface QuantityTypeWithOffer {
  buyXGetYEnabled?: boolean;
  buyXGetY?: BuyXGetYApiOffer;
  buyXGetYOffers?: BuyXGetYApiOffer[];
}

const pad = (value: number) => String(value).padStart(2, '0');

const toDateInputValue = (value?: string | Date) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const apiDate = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (apiDate) return apiDate[1];
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const emptyOffer = (): BuyXGetYOffer => ({
  buyQty: '',
  freeQty: '',
  validFrom: '',
  validTo: '',
  isRepeatable: true,
});

export const emptyBuyXGetY = (): BuyXGetYOffer[] => [emptyOffer()];

const getStoredOffers = (quantityType: QuantityTypeWithOffer) =>
  (quantityType?.buyXGetYOffers?.length
    ? quantityType.buyXGetYOffers
    : quantityType?.buyXGetY
      ? [quantityType.buyXGetY]
      : []
  ).filter((offer) => offer?.active !== false && offer?.active !== 'false');

export const hasBuyXGetY = (quantityType: QuantityTypeWithOffer) =>
  !!quantityType?.buyXGetYEnabled && getStoredOffers(quantityType).length > 0;

export const buyXGetYFromApi = (
  quantityType: QuantityTypeWithOffer
): BuyXGetYOffer[] => {
  const offers = getStoredOffers(quantityType);
  if (!quantityType?.buyXGetYEnabled || !offers.length) return emptyBuyXGetY();
  return offers.map((offer) => ({
    buyQty: offer.buyQty !== undefined ? String(offer.buyQty) : '',
    freeQty: offer.freeQty !== undefined ? String(offer.freeQty) : '',
    validFrom: toDateInputValue(offer.validFrom),
    validTo: toDateInputValue(offer.validTo),
    isRepeatable: offer.isRepeatable !== false,
  }));
};

const isPositiveInteger = (value: string) =>
  /^\d+$/.test(String(value ?? '').trim()) &&
  Number.isSafeInteger(Number(value)) && Number(value) >= 1;

const getDuplicateOfferIndexes = (offers: BuyXGetYOffer[]) => {
  const duplicateIndexes = new Set<number>();
  for (let index = 0; index < offers.length; index += 1) {
    for (let nextIndex = index + 1; nextIndex < offers.length; nextIndex += 1) {
      const first = offers[index];
      const second = offers[nextIndex];
      if (
        isPositiveInteger(first.buyQty) &&
        isPositiveInteger(first.freeQty) &&
        Number(first.buyQty) === Number(second.buyQty) &&
        Number(first.freeQty) === Number(second.freeQty) &&
        first.validFrom === second.validFrom &&
        first.validTo === second.validTo &&
        (first.isRepeatable !== false) === (second.isRepeatable !== false)
      ) {
        duplicateIndexes.add(index);
        duplicateIndexes.add(nextIndex);
      }
    }
  }
  return duplicateIndexes;
};

/** Returns an error message, or '' when the offer is valid. */
export const validateBuyXGetY = (
  offerValues?: BuyXGetYOffer | BuyXGetYOffer[]
): string => {
  const offers = Array.isArray(offerValues) ? offerValues : offerValues ? [offerValues] : [];
  if (!offers.length) return 'At least one offer tier is required';

  for (const offer of offers) {
    if (!isPositiveInteger(offer.buyQty) || !isPositiveInteger(offer.freeQty)) {
      return 'Buy quantity and free quantity must be whole numbers greater than 0';
    }
    if ([offer.validFrom, offer.validTo].some(value => {
      if (!value) return false;
      const date = new Date(`${value}T00:00:00.000Z`);
      return !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
        Number.isNaN(date.getTime()) ||
        date.toISOString().slice(0, 10) !== value;
    })) {
      return 'Valid from and valid to must be valid calendar dates';
    }
    if (offer.validFrom && offer.validTo && offer.validFrom > offer.validTo) {
      return 'Valid from date must be on or before valid to date';
    }
    if (
      offer.touched &&
      offer.validTo &&
      offer.validTo < getBusinessDay()
    ) {
      return 'Valid to date cannot be in the past';
    }
  }

  if (getDuplicateOfferIndexes(offers).size > 0) {
    return 'Duplicate offer tiers are not allowed';
  }

  for (let index = 0; index < offers.length; index += 1) {
    for (let nextIndex = index + 1; nextIndex < offers.length; nextIndex += 1) {
      const first = offers[index];
      const second = offers[nextIndex];
      const datesOverlap =
        (!first.validTo || !second.validFrom || first.validTo >= second.validFrom) &&
        (!second.validTo || !first.validFrom || second.validTo >= first.validFrom);
      if (Number(first.buyQty) === Number(second.buyQty) && datesOverlap) {
        return 'Offer tiers with the same buy quantity cannot have overlapping validity dates';
      }
    }
  }
  return '';
};

export const isBuyXGetYDiff = (
  first?: BuyXGetYOffer | BuyXGetYOffer[],
  second?: BuyXGetYOffer | BuyXGetYOffer[]
) => {
  const normalize = (value?: BuyXGetYOffer | BuyXGetYOffer[]) =>
    (Array.isArray(value) ? value : value ? [value] : []).map((offer) => ({
      buyQty: offer.buyQty,
      freeQty: offer.freeQty,
      validFrom: offer.validFrom,
      validTo: offer.validTo,
      isRepeatable: offer.isRepeatable !== false,
    }));
  return JSON.stringify(normalize(first)) !== JSON.stringify(normalize(second));
};

/** UTC start/end of the selected calendar days, independent of the browser timezone. */
export const buyXGetYToPayload = (offers: BuyXGetYOffer[]) =>
  offers.map((offer) => ({
    buyQty: String(Number(offer.buyQty)),
    freeQty: String(Number(offer.freeQty)),
    ...(offer.validFrom ? { validFrom: new Date(`${offer.validFrom}T00:00:00.000Z`).toISOString() } : {}),
    ...(offer.validTo ? { validTo: new Date(`${offer.validTo}T23:59:59.999Z`).toISOString() } : {}),
    isRepeatable: String(offer.isRepeatable !== false),
  }));

interface BuyXGetYOfferFieldsProps {
  offer: BuyXGetYOffer[];
  unitLabel: string;
  showError: boolean;
  onChange: (offer: BuyXGetYOffer[]) => void;
}

export const BuyXGetYOfferFields = ({
  offer,
  unitLabel,
  showError,
  onChange,
}: BuyXGetYOfferFieldsProps) => {
  const validationMessage = validateBuyXGetY(offer);
  const duplicateIndexes = getDuplicateOfferIndexes(offer);
  const errorMessage = showError || duplicateIndexes.size > 0 ? validationMessage : '';
  const canAddOffer = offer.every((tier) => !validateBuyXGetY([tier])) && !validationMessage;
  const update = (index: number, patch: Partial<BuyXGetYOffer>) =>
    onChange(offer.map((tier, tierIndex) =>
      tierIndex === index ? { ...tier, ...patch, touched: true } : tier
    ));
  const addOffer = () => onChange([...offer, emptyOffer()]);
  const removeOffer = (index: number) =>
    onChange(offer.filter((_, tierIndex) => tierIndex !== index));

  return (
    <>
      <div className="separator my-2" />
      <Row className="px-6 pt-4 pb-6">
        <Col lg={12}>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h2 className="fs-18 fw-bolder text-dark mb-0">Buy X Get Y free offers</h2>
            <button
              type="button"
              className="btn btn-sm btn-light-primary"
              onClick={addOffer}
              disabled={!canAddOffer}
              title={canAddOffer ? 'Add offer tier' : 'Complete and correct existing offer tiers before adding another'}
            >
              <Plus size={16} aria-hidden="true" /> Add offer
            </button>
          </div>
          {!canAddOffer ? (
            <div className="fs-12 fw-500 text-muted mb-3" role="status">
              Complete and correct existing offer tiers before adding another.
            </div>
          ) : null}
          {offer.map((tier, index) => {
            const isDuplicate = duplicateIndexes.has(index);
            const tierHasError = isDuplicate || (showError && !!validateBuyXGetY([tier]));
            const preview = isPositiveInteger(tier.buyQty) && isPositiveInteger(tier.freeQty)
              ? `Buy ${tier.buyQty} ${unitLabel}, get ${tier.freeQty} ${unitLabel} free${tier.isRepeatable === false ? ' once' : ` for every ${tier.buyQty}`}`
              : '';
            return (
              <div
                className={clsx('border rounded p-4 mb-3', isDuplicate ? 'border-danger' : '')}
                key={`offer-${index}`}
                aria-invalid={isDuplicate}
              >
                <Row className="align-items-end">
                  <Col lg={2} sm={6} className="mb-3">
                    <label className="fs-14 fw-500 mb-2 required">Buy qty.</label>
                    <div className="input-group input-group-solid border">
                      <input
                        type="text"
                        inputMode="numeric"
                        className={clsx('form-control border-0 bg-white text-black', tierHasError && !isPositiveInteger(tier.buyQty) ? 'is-invalid' : '')}
                        placeholder="e.g. 10"
                        value={tier.buyQty}
                        onChange={(event) => update(index, { buyQty: event.target.value })}
                      />
                      <span className="input-group-text bg-white border-0">{unitLabel}</span>
                    </div>
                  </Col>
                  <Col lg={2} sm={6} className="mb-3">
                    <label className="fs-14 fw-500 mb-2 required">Free qty.</label>
                    <div className="input-group input-group-solid border">
                      <input
                        type="text"
                        inputMode="numeric"
                        className={clsx('form-control border-0 bg-white text-black', tierHasError && !isPositiveInteger(tier.freeQty) ? 'is-invalid' : '')}
                        placeholder="e.g. 2"
                        value={tier.freeQty}
                        onChange={(event) => update(index, { freeQty: event.target.value })}
                      />
                      <span className="input-group-text bg-white border-0">{unitLabel}</span>
                    </div>
                  </Col>
                  <Col lg={3} sm={6} className="mb-3">
                    <label className="fs-14 fw-500 mb-2">Valid from</label>
                    <CustomDatePicker
                      className="form-control bg-white text-black"
                      selected={tier.validFrom ? new Date(`${tier.validFrom}T00:00:00`) : null}
                      onChange={(date: Date | null) => update(index, { validFrom: date ? toDateInputValue(date) : '' })}
                      dateFormat="dd/MM/yyyy"
                      showFullMonthYearPicker={true}
                      maxDate={tier.validTo ? new Date(`${tier.validTo}T23:59:59.999`) : undefined}
                      isClearable={true}
                      placeholderText="Select date"
                    />
                  </Col>
                  <Col lg={3} sm={6} className="mb-3">
                    <label className="fs-14 fw-500 mb-2">Valid to</label>
                    <CustomDatePicker
                      className="form-control bg-white text-black"
                      selected={tier.validTo ? new Date(`${tier.validTo}T00:00:00`) : null}
                      onChange={(date: Date | null) => update(index, { validTo: date ? toDateInputValue(date) : '' })}
                      dateFormat="dd/MM/yyyy"
                      showFullMonthYearPicker={true}
                      minDate={tier.validFrom ? new Date(`${tier.validFrom}T00:00:00`) : undefined}
                      isClearable={true}
                      placeholderText="Select date"
                    />
                  </Col>
                  <Col lg={2} sm={12} className="mb-3 d-flex justify-content-lg-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-light-danger"
                      aria-label={`Delete offer tier ${index + 1}`}
                      title="Delete offer tier"
                      disabled={offer.length === 1}
                      onClick={() => removeOffer(index)}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </Col>
                </Row>
                {preview ? <div className="fs-13 fw-500 text-muted">{preview}</div> : null}
                {isDuplicate ? (
                  <div className="fs-12 fw-500 text-danger mt-2" role="alert">
                    Duplicate offer tier
                  </div>
                ) : null}
              </div>
            );
          })}
          {errorMessage ? (
            <div className="fs-12 fw-500 text-danger mt-2">{errorMessage}</div>
          ) : null}
        </Col>
      </Row>
    </>
  );
};
